import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MAX_DISCOUNT_PERCENT } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { PageHero } from "@/shared/ui/site";
import { useHeroMotion } from "@/features/shop/components/hero/useHeroMotion";
import { PackagesHeroDial } from "./PackagesHeroDial";

export interface PackagesHeroProps {
  /** El ancla de la rejilla, para la acción de bajar. */
  gridHref: string;
}

/**
 * La portada de la rejilla de paquetes.
 *
 * ── Qué era y por qué cambió ─────────────────────────────────────────
 * Era un bloque de texto CENTRADO sobre fondo plano con un halo lila
 * detrás. Anahi lo señaló el 26-ago: el sitio tiene dos patrones de
 * portada —el de `/franquicias` (texto a la izquierda, algo vivo a la
 * derecha) y el de `/shop` (héroe con vídeo de fondo)— y esto no era
 * ninguno de los dos.
 *
 * ── Se eligió el patrón de `/franquicias`. Por qué ───────────────────
 * A primera vista `/paquetes` es un catálogo y le tocaría el patrón de
 * `/shop`. No: el héroe de `/shop` ES el vídeo de marca a sangre, el
 * mismo bucle de `itsthebest1111.com`. Puesto también aquí, las dos
 * páginas abrirían con el mismo plano y `/paquetes` parecería la tienda
 * otra vez —justo cuando lo que tiene que quedar claro es que aquí se
 * compra de OTRA manera, una vez al mes y con descuento—.
 *
 * El patrón de `/franquicias`, en cambio, deja media portada libre para
 * enseñar algo, y esta página tiene algo concreto que enseñar: la escala
 * de descuento. Y coincide con lo que pidió Anahi al pie de la letra —«el
 * header de /franquicias con diferentes animaciones a la derecha»—: misma
 * estructura, pieza distinta. Aquí la pieza es `PackagesHeroDial` y no el
 * vídeo circular.
 *
 * ── La estructura ya no vive en este archivo ─────────────────────────
 * La rejilla asimétrica, la columna de texto y la caja de la derecha son
 * `PageHero`, en `shared/ui/site`. Escritas aquí, la tercera página que
 * quisiera esta portada las copiaría — y este repositorio tiene 296
 * componentes precisamente porque no se copian.
 *
 * ── El titular va partido en dos claves ──────────────────────────────
 * No lo está: `PageHero` recibe la frase ENTERA y busca dentro qué
 * resaltar (`splitHighlight`). Es lo contrario de lo que había, que
 * mandaba dos claves de traducción — y una frase partida en dos claves no
 * se puede traducir, porque en inglés el resalte cae en otras palabras.
 *
 * ── La escala de descuento se lee, no se grita ───────────────────────
 * El objetivo declarado del negocio es que la gente se vuelva
 * franquiciataria, y el argumento es que el techo del descuento sube del
 * 15% al 30%. Va como una frase en la entradilla —no como una insignia
 * roja ni un contador— porque quien llega buscando su paquete mensual no
 * viene a que le vendan una franquicia; viene a comprar. Que lo lea de
 * paso es suficiente: la pestaña de negocio digital y el cierre de la
 * página lo repiten cuando ya sabe de qué se trata.
 *
 * El disco de la derecha enseña los mismos dos números, y por eso puede
 * ser decoración: no añade nada que la entradilla no diga.
 *
 * Los dos números salen de `MAX_DISCOUNT_PERCENT`, que es el contrato.
 * Escritos a mano en el texto, el día que la banda cambie esta frase
 * mentiría y nadie lo notaría hasta que alguien sacara la calculadora.
 */
export function PackagesHero({ gridHref }: PackagesHeroProps) {
  const { t } = useTranslation();

  /*
   * La preferencia se consulta AQUÍ y baja como prop, igual que en
   * `ShopHero` y en `FranchisePage`: una sola suscripción a la media
   * query por portada. Con el hook repetido dentro de cada pieza que se
   * mueve habría varias, y la posibilidad de que una se quedara sin
   * enterarse de un cambio y siguiera animando sola.
   */
  const motionOk = useHeroMotion();

  return (
    <PageHero
      eyebrow={t("Paquetes mensuales")}
      title={t("Tu compra de cada mes, a precio de paquete")}
      highlight={t("a precio de paquete")}
      description={t(
        "Un paquete es tu recompra mensual con descuento: eliges los productos una vez y se te cobran mes con mes. Ahorras hasta un {{personal}}%, y con franquicia digital hasta un {{franquicia}}%.",
        {
          personal: MAX_DISCOUNT_PERCENT.personal,
          franquicia: MAX_DISCOUNT_PERCENT.franchisee,
        },
      )}
      actions={
        <Button
          as="a"
          href={gridHref}
          size="lg"
          rightIcon={<ArrowRight aria-hidden="true" />}
        >
          {t("Ver los paquetes")}
        </Button>
      }
      aside={<PackagesHeroDial motionOk={motionOk} />}
    />
  );
}
